import type { ReactNode } from 'react';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import ShareEvidencePackModal from '../../components/sharing/ShareEvidencePackModal';

type EvidenceFormat = 'pdf' | 'csv' | 'json' | 'manifest';
type RunStatus = 'idle' | 'generating' | 'completed' | 'error';

/**
 * Reusable hero section component for tool pages
 */
interface ToolHeroProps {
  title: string;
  subtitle: string;
  description: string;
  ctaText: string;
  onCta: () => void;
}

export const ToolHero = ({ title, subtitle, description, ctaText, onCta }: ToolHeroProps) => {
  return (
    <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16 px-6 sm:py-20 sm:px-8">
      <div className="max-w-3xl mx-auto text-center">
        <p className="text-blue-200 text-sm font-semibold uppercase tracking-wider mb-2">
          {subtitle}
        </p>
        <h1 className="text-4xl sm:text-5xl font-bold mb-4">{title}</h1>
        <p className="text-lg text-blue-100 mb-8">{description}</p>
        <button
          onClick={onCta}
          className="inline-block bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition"
        >
          {ctaText}
        </button>
        <p className="text-blue-200 text-sm mt-4">
          🚀 Generates auditor-ready evidence in under 60 seconds
        </p>
      </div>
    </div>
  );
};

/**
 * Form component for tool inputs
 */
interface ToolFormProps {
  children: ReactNode;
  onSubmit: (data: any) => void;
  isLoading: boolean;
}

export const ToolForm = ({ children, onSubmit, isLoading }: ToolFormProps) => {
  return (
    <form onSubmit={(e) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      const data = Object.fromEntries(formData);
      onSubmit(data);
    }} className="space-y-6">
      {children}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition"
      >
        {isLoading ? 'Generating...' : 'Generate Report'}
      </button>
    </form>
  );
};

/**
 * Status display component for ongoing/completed evidence pack generation
 */
interface ToolRunStatusProps {
  status: RunStatus;
  progress?: number;
  message?: string;
  error?: string;
}

export const ToolRunStatus = ({ status, progress, message, error }: ToolRunStatusProps) => {
  if (status === 'idle') return null;

  return (
    <div className="mt-8 p-6 rounded-lg bg-gray-50 border border-gray-200">
      <div className="flex items-center space-x-3 mb-4">
        {status === 'generating' && (
          <>
            <div className="animate-spin h-5 w-5 text-blue-600"></div>
            <p className="text-gray-700 font-semibold">Generating...</p>
          </>
        )}
        {status === 'completed' && (
          <>
            <span className="text-2xl">✅</span>
            <p className="text-gray-700 font-semibold">Report Generated Successfully</p>
          </>
        )}
        {status === 'error' && (
          <>
            <span className="text-2xl">❌</span>
            <p className="text-red-600 font-semibold">Generation Failed</p>
          </>
        )}
      </div>

      {progress !== undefined && status === 'generating' && (
        <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      )}

      {message && <p className="text-gray-600 text-sm mb-2">{message}</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}
    </div>
  );
};

/**
 * Evidence pack download section
 */
interface EvidencePackDownloadsProps {
  packId: string;
  checksum: string;
  formats: EvidenceFormat[];
  onDownload: (format: EvidenceFormat) => void;
  showShareButton?: boolean;
}

export const EvidencePackDownloads = ({ packId, checksum, formats, onDownload, showShareButton = true }: EvidencePackDownloadsProps) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  return (
    <>
      <div className="mt-8 p-6 rounded-lg bg-blue-50 border border-blue-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Download Evidence Pack</h3>
          {showShareButton && (
            <button
              onClick={() => setIsShareModalOpen(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition"
            >
              Share
            </button>
          )}
        </div>
        
        <div className="mb-4 p-3 bg-white rounded border border-gray-200">
          <p className="text-xs text-gray-600 mb-1">SHA-256 Checksum</p>
          <p className="font-mono text-sm break-all text-gray-900">{checksum}</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {formats.map((format: EvidenceFormat) => (
            <button
              key={format}
              onClick={() => onDownload(format)}
              className="p-3 rounded-lg bg-white border border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition text-center"
            >
              <span className="block text-2xl mb-1">
                {format === 'pdf' && '📄'}
                {format === 'csv' && '📊'}
                {format === 'json' && '{}'}
                {format === 'manifest' && '📋'}
              </span>
              <span className="block text-sm font-semibold text-gray-900 uppercase">{format}</span>
            </button>
          ))}
        </div>
      </div>
      
      {showShareButton && (
        <ShareEvidencePackModal
          packId={packId}
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
        />
      )}
    </>
  );
};

/**
 * Checksum badge/display component
 */
interface ChecksumBadgeProps {
  checksum: string;
  displayFormat?: 'short' | 'full';
}

export const ChecksumBadge = ({ checksum, displayFormat = 'short' }: ChecksumBadgeProps) => {
  const display = displayFormat === 'short'
    ? `${checksum.substring(0, 12)}...${checksum.substring(checksum.length - 8)}`
    : checksum;

  return (
    <code className="inline-block px-3 py-1 rounded-full bg-gray-100 text-gray-800 text-xs font-mono border border-gray-300">
      {display}
    </code>
  );
};

/**
 * SEO meta tags for tool pages
 */
interface ToolPageMetaProps {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogImage?: string;
}

export const ToolPageMeta = ({ title, description, keywords, canonical, ogImage }: ToolPageMetaProps) => {
  return (
    <Helmet>
      <title>{title} | EvidentTrail</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="canonical" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
    </Helmet>
  );
};
